// Sijoitukset-välilehti: oma sivunsa, jotta tulokset eivät huku Passin joukkoon.
//
// Rakenne ylhäältä alas:
//   1. kausi (Kaikki / Viikko / Kuukausi), yhteinen molemmille tauluille ja muistetaan puhelimessa
//   2. taulun valinta: Pelaajat | Joukkueet ja perheet
//   3. oma tilanne isolla kortilla (sija, pisteet, ero kärkeen)
//   4. palkintokoroke kolmelle parhaalle
//   5. koko lista (oma rivi korostettuna)
// Luvut ja sijat laskee palvelin; tässä ne vain asetellaan. Puhdas logiikka on lib/leaderboard.js.

import { useEffect, useState } from "preact/hooks";

import {
  PERIODS,
  gapLine,
  medal,
  periodHint,
  splitPodium,
  statParts,
  teamGapLine,
  teamKindLabel,
  teamStatParts,
  winnerInfo,
} from "../lib/leaderboard.js";
import { storage } from "../lib/storage.js";
import { PlayerChip } from "./PlayerViews.jsx";
import { TeamSheet } from "./TeamViews.jsx";

const fi = (n) => n.toLocaleString("fi-FI");
const TEAM_EMOJI = { family: "🏡", team: "🚩" };

/** Pelaajan tai joukkueen kuvake: pelaajalla oma tunnus ja väri, joukkueella tyypin symboli. */
function Badge({ row, kind, large }) {
  if (kind === "players") return <PlayerChip player={row} large={large} />;
  return (
    <span class={`player-chip ${large ? "large" : ""}`} aria-hidden="true">
      {TEAM_EMOJI[row.kind] ?? TEAM_EMOJI.team}
    </span>
  );
}

/** Oma tilanne: iso sija, pisteet ja ero kärkeen. `mine` on oma rivi tai null, kun omaa riviä ei ole. */
function Hero({ kind, mine, total, gap, onJoin }) {
  if (!mine) {
    return (
      <section class="lb-hero empty" aria-label="Oma tilanne">
        {kind === "teams" ? (
          <>
            <p class="lb-hero-title">Et kuulu vielä joukkueeseen tai perheeseen</p>
            <p class="s">Perusta tai liity Passin Oma joukkue -kortista, niin pisteesi kartuttavat myös yhteistä tulosta.</p>
            <button class="btn btn-rasti" onClick={onJoin}>Avaa Passi</button>
          </>
        ) : (
          <p class="lb-hero-title">Sinulla ei ole vielä sijaa tässä taulussa</p>
        )}
      </section>
    );
  }
  return (
    <section class="lb-hero" aria-label="Oma tilanne">
      <div class="lb-hero-rank" aria-label={`Sija ${mine.rank} / ${total}`}>
        <span class="lb-rank-num">{mine.rank}.</span>
        <span class="s">/ {total}</span>
      </div>
      <div class="lb-hero-body">
        <b class="lb-hero-name">{kind === "teams" ? `${teamKindLabel(mine)}: ${mine.name}` : mine.name}</b>
        {gap && <p class="board-gap" aria-live="polite">{gap}</p>}
      </div>
      <div class="pts"><b class="num">{fi(mine.points)}</b><span>pistettä</span></div>
    </section>
  );
}

/** Kolme parasta korokkeella; keskimmäinen (1.) korkeimmalla. Tasatilanteessa sama sija saa saman värin. */
function Podium({ kind, rows, isMine, onOpen }) {
  if (rows.length === 0) return null;
  // Visuaalinen järjestys 2., 1., 3. Yksittäinen tai kaksi ensimmäistä sopivat samaan asetteluun.
  const order = rows.length >= 3 ? [rows[1], rows[0], rows[2]] : rows;
  return (
    <ol class="lb-podium" aria-label="Kolme parasta">
      {order.map((row) => {
        const open = onOpen ? () => onOpen(row) : undefined;
        const Tag = open ? "button" : "div";
        return (
          <li key={row.id} class={`lb-step p${Math.min(row.rank, 3)} ${isMine(row) ? "me" : ""}`} style={{ "--pc": row.color }}>
            <Tag class="lb-step-btn" onClick={open} aria-label={open ? `Avaa ${row.name}` : undefined}>
              <Badge row={row} kind={kind} large />
              <b class="lb-step-name">{row.name}</b>
              <span class="lb-step-pts"><b class="num">{fi(row.points)}</b> p</span>
              <span class={`rank ${medal(row)}`} aria-label={`Sija ${row.rank}`}>{row.rank}</span>
            </Tag>
          </li>
        );
      })}
    </ol>
  );
}

/** Yksi rivi listassa; joukkuerivin napautus avaa joukkueen. */
function Row({ kind, row, mine, onOpen }) {
  const parts = kind === "players" ? statParts(row) : teamStatParts(row);
  const name = (
    <>
      <b>{row.name}{mine ? " (sinä)" : ""}</b>
      <div class="s">
        {kind === "teams" && <span class="lb-kind">{teamKindLabel(row)}</span>}
        {parts.map((part) => <span key={part}>{part}</span>)}
      </div>
    </>
  );
  return (
    <li class={`board-row ${mine ? "me" : ""}`} style={{ "--pc": row.color ?? "var(--rasti)" }}>
      <span class={`rank ${medal(row)}`} aria-label={`Sija ${row.rank}`}>{row.rank}</span>
      <Badge row={row} kind={kind} large />
      {onOpen ? (
        <button class="who lb-open" onClick={() => onOpen(row)} aria-label={`Avaa ${row.name}`}>{name}</button>
      ) : (
        <div class="who">{name}</div>
      )}
      <div class="pts"><b class="num">{fi(row.points)}</b><span>p</span></div>
    </li>
  );
}

export function LeaderboardPage({ app }) {
  const saved = storage.get("boardPeriod", "all");
  const [period, setPeriod] = useState(PERIODS.some((p) => p.id === saved) ? saved : "all");
  const [kind, setKind] = useState("players");
  const [board, setBoard] = useState(null);
  const [winners, setWinners] = useState(null);
  const [failed, setFailed] = useState(false);
  const [openTeam, setOpenTeam] = useState(null);

  // Taulu haetaan, kun kausi, taulun tyyppi tai pelin tiedot muuttuvat. Vanha vastaus ei ylikirjoita uudempaa.
  useEffect(() => {
    let alive = true;
    setFailed(false);
    const load = kind === "teams" ? app.api.teamLeaderboard(period) : app.api.leaderboard(period);
    load.then(
      (data) => alive && setBoard({ kind, period, rows: kind === "teams" ? data.teams : data.players, from: data.from, until: data.until }),
      () => alive && setFailed(true),
    );
    return () => {
      alive = false;
    };
  }, [kind, period, app.dataVersion]);

  // Edellisen kauden voittajat (vain pelaajille); virhe ei estä taulua.
  useEffect(() => {
    let alive = true;
    app.api.leaderboardWinners().then((d) => alive && setWinners(d), () => alive && setWinners(null));
    return () => {
      alive = false;
    };
  }, [app.dataVersion]);

  const pickPeriod = (id) => {
    setPeriod(id);
    storage.set("boardPeriod", id);
  };

  // Näytetään vain vastaus, joka vastaa nykyistä valintaa; väärän kauden lukuja ei esitetä uuden otsikon alla.
  const current = board && board.kind === kind && board.period === period ? board : null;
  const rows = current?.rows ?? [];
  const myTeamId = app.membership?.team_id ?? null;
  const isMine = (row) => (kind === "players" ? row.id === app.activePlayer : row.id === myTeamId);
  const mine = rows.find(isMine) ?? null;
  const gap = kind === "players" ? gapLine(rows, app.activePlayer) : teamGapLine(rows, myTeamId);
  const { podium, rest } = splitPodium(rows);
  const last = kind === "players" && period !== "all" ? winnerInfo(period, winners?.[period], app.activePlayer) : null;
  const open = kind === "teams" ? (row) => setOpenTeam(row.id) : undefined;

  return (
    <div class="page">
      <div class="page-inner lb-page">
        <h1>Sijoitukset</h1>
        <div class="seg" role="group" aria-label="Taulu">
          <button aria-pressed={kind === "players"} onClick={() => setKind("players")}>Pelaajat</button>
          <button aria-pressed={kind === "teams"} onClick={() => setKind("teams")}>Joukkueet ja perheet</button>
        </div>
        <div class="seg" role="group" aria-label="Kausi">
          {PERIODS.map((p) => (
            <button key={p.id} aria-pressed={period === p.id} onClick={() => pickPeriod(p.id)}>{p.label}</button>
          ))}
        </div>
        <p class="s lb-period">{periodHint(current)}</p>

        {failed && !current && <p class="notice warn" role="alert">Sijoituksia ei saatu ladattua.</p>}
        {!current && !failed && <p role="status">Ladataan tuloksia…</p>}
        {current && (
          <>
            <Hero kind={kind} mine={mine} total={rows.length} gap={gap} onJoin={() => app.goTo("pass")} />
            {last && (
              <p class={`board-last ${last.mine ? "mine" : ""}`}>
                <b>{last.title}</b> {last.text}
              </p>
            )}
            {kind === "teams" && (
              <p class="s">Joukkueen pisteet ovat jäsenyyden aikana ansaittuja. Eroaminen ei siirrä aiempia pisteitä uudelle joukkueelle.</p>
            )}
            {rows.length === 0 && (
              <p>{kind === "teams" ? "Joukkueita ei vielä ole. Voit perustaa ensimmäisen Passista." : "Pelaajia ei vielä ole."}</p>
            )}
            <Podium kind={kind} rows={podium} isMine={isMine} onOpen={open} />
            {rest.length > 0 && (
              <ol class="board lb-rest">
                {rest.map((row) => <Row key={row.id} kind={kind} row={row} mine={isMine(row)} onOpen={open} />)}
              </ol>
            )}
          </>
        )}
        {openTeam && <TeamSheet app={app} id={openTeam} onClose={() => setOpenTeam(null)} />}
      </div>
    </div>
  );
}
